import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import type {
  Vehicle,
  VehicleListResponse,
  VehicleListParams,
  VehicleInput,
  LegalEntity,
} from '@/lib/types';

export async function fetchVehicles(params: VehicleListParams): Promise<VehicleListResponse> {
  const { data } = await apiClient.get<VehicleListResponse>('/api/v1/vehicles', { params });
  return data;
}

export async function fetchVehicle(id: string): Promise<Vehicle> {
  const { data } = await apiClient.get<{ vehicle: Vehicle }>(`/api/v1/vehicles/${id}`);
  return data.vehicle;
}

export async function createVehicle(input: VehicleInput): Promise<Vehicle> {
  const { data } = await apiClient.post<{ vehicle: Vehicle }>('/api/v1/vehicles', input);
  return data.vehicle;
}

export async function updateVehicle(id: string, input: Partial<VehicleInput>): Promise<Vehicle> {
  const { data } = await apiClient.patch<{ vehicle: Vehicle }>(`/api/v1/vehicles/${id}`, input);
  return data.vehicle;
}

export async function fetchLegalEntities(): Promise<LegalEntity[]> {
  const { data } = await apiClient.get<{ items: LegalEntity[] }>('/api/v1/legal-entities');
  return data.items;
}

export async function fetchExpiringDocuments(windowDays: number = 30) {
  const { data } = await apiClient.get<{ items: Array<{ id: string; code: string; plateEn: string; brand: string | null; model: string | null; istimaraExpiry: string | null; insuranceExpiry: string | null; naqliatCardExpiryDate: string | null; inspectionNextDueDate: string | null; daysLeft: number | null; urgency: 'expired' | 'critical' | 'warning' | 'ok' }>; total: number }>(
    '/api/v1/vehicles/expiring-documents',
    { params: { windowDays } }
  );
  return data;
}

export function useVehicles(params: VehicleListParams) {
  return useQuery({
    queryKey: ['vehicles', params],
    queryFn: () => fetchVehicles(params),
    placeholderData: (prev) => prev,
  });
}

export function useVehicle(id: string | null | undefined) {
  return useQuery({
    queryKey: ['vehicle', id],
    queryFn: () => fetchVehicle(id!),
    enabled: !!id,
  });
}

export function useCreateVehicle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createVehicle,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vehicles'] }),
  });
}

export function useUpdateVehicle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<VehicleInput> }) =>
      updateVehicle(id, input),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ['vehicles'] });
      qc.setQueryData(['vehicle', updated.id], updated);
    },
  });
}

export function useLegalEntities() {
  return useQuery({
    queryKey: ['legal-entities'],
    queryFn: fetchLegalEntities,
    staleTime: 60 * 60 * 1000,
  });
}

export function useAllInvestors() {
  return useQuery({
    queryKey: ['investors-all'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ items: Array<{ id: string; code: string; fullNameEn: string; fullNameAr: string | null; phone: string }> }>(
        '/api/v1/investors',
        { params: { pageSize: 100, status: 'active' } }
      );
      return data.items;
    },
    staleTime: 60 * 1000,
  });
}

export function useExpiringDocuments(windowDays: number = 30) {
  return useQuery({
    queryKey: ['expiring-documents', windowDays],
    queryFn: () => fetchExpiringDocuments(windowDays),
    staleTime: 60 * 1000,
  });
}
